/**
 * Privacy Policy / Integritetspolicy
 * Source: /Information/Terms/gdpr_policy_v1_*.md
 */

export const privacyPolicySV = `# Integritetspolicy för Beredskapshubben

**Senast uppdaterad:** 2026-03-07

Beredskapshubben värnar om din personliga integritet. Denna integritetspolicy förklarar hur vi samlar in, använder och skyddar personuppgifter när du använder vår tjänst.

Den gäller för webbplats, mobilapplikation och relaterade tjänster ("Tjänsten").

---

# 1. Personuppgiftsansvarig

Personuppgiftsansvarig är:

**Beredskapshubben Norden AB**
Org.nr: 559575-3343
Adress: Örebro, Sverige
E-post: [privacy@beredskapshubben.se](mailto:privacy@beredskapshubben.se)

---

# 2. Vad är personuppgifter?

Personuppgifter är information som direkt eller indirekt kan kopplas till en levande person, exempelvis:

* namn
* e-postadress
* användar-ID
* IP-adress
* enhetsinformation
* gruppmedlemskap
* användarbeteende i appen

---

# 3. Vilka uppgifter vi samlar in

## 3.1 Uppgifter du själv lämnar

När du registrerar och använder Tjänsten:

* namn eller visningsnamn
* e-postadress
* inloggningsuppgifter (engångskoder via e-post; inga permanenta lösenord lagras)
* profilinformation
* beredskapslistor och anteckningar
* lagerlistor
* grupp- och familjeinformation
* meddelanden och chattinnehåll
* supportärenden

---

## 3.2 Uppgifter som skapas vid användning

* aktivitetsloggar
* funktionsanvändning
* tidsstämplar
* ändringshistorik
* gruppinteraktioner

---

## 3.3 Tekniska uppgifter

* IP-adress
* enhetstyp
* operativsystem
* appversion
* sessionsdata
* felrapporter och krascher

---

## 3.4 Betalningsuppgifter

Vid Premiumköp:

* prenumerationsstatus
* betalningsreferens
* transaktions-ID

Kortuppgifter hanteras av extern betalleverantör och lagras inte hos oss.

---

# 4. Ändamål med behandlingen

Vi behandlar personuppgifter för att:

* tillhandahålla Tjänsten
* skapa och hantera konton
* möjliggöra familje- och gruppfunktioner
* lagra checklistor och beredskapsdata
* leverera notiser och påminnelser
* hantera betalningar
* ge support
* förbättra funktioner och användarupplevelse
* förebygga missbruk och säkerhetshot
* uppfylla lagkrav

---

# 5. Laglig grund enligt GDPR

Vi behandlar personuppgifter med stöd av:

## Avtal

För att leverera Tjänsten enligt användarvillkoren.

## Berättigat intresse

För:

* säkerhet
* missbruksförebyggande
* driftanalys
* produktförbättring

## Rättslig förpliktelse

För bokföring och lagkrav.

## Samtycke

För:

* frivilliga funktioner
* vissa analyser
* marknadsföring (om tillämpligt)

---

# 6. Sociala och gruppbaserade funktioner

När du använder grupp- och familjefunktioner:

* delas innehåll med de medlemmar du bjuder in
* gruppadministratörer kan se gruppdata
* du ansvarar för vad du delar

Vi rekommenderar att inte dela känsliga personuppgifter.

---

# 7. Lagringstid

Vi sparar personuppgifter så länge det behövs för ändamålet:

* Konto: tills det avslutas
* Innehåll: tills radering begärs eller konto avslutas
* Supportärenden: upp till 24 månader
* Betalningsunderlag: enligt bokföringslag (7 år)
* Säkerhetsloggar: normalt 6–12 månader

Data kan anonymiseras för statistik.

---

# 8. Mottagare av uppgifter

Vi kan dela uppgifter med:

## Personuppgiftsbiträden

* molnleverantör
* driftleverantör
* analysverktyg
* e-posttjänster
* betalningsleverantör

Alla biträden har personuppgiftsbiträdesavtal.

---

## Myndigheter

Vid lagkrav eller rättslig begäran.

---

# 9. Tredjelandsöverföring

Om leverantörer finns utanför EU/EES används:

* EU:s standardavtalsklausuler
* adekvansbeslut
* andra godkända skyddsåtgärder

---

# 10. Säkerhet

Vi använder tekniska och organisatoriska skyddsåtgärder:

* krypterad kommunikation (TLS)
* åtkomstkontroller
* loggning
* behörighetsstyrning
* säkerhetsövervakning
* skydd av inloggningsuppgifter
* principen om minsta privilegium

---

# 11. Dina rättigheter enligt GDPR

Du har rätt att:

* få registerutdrag
* få rättelse
* få radering
* begränsa behandling
* invända mot behandling
* få dataportabilitet
* återkalla samtycke
* klaga till Integritetsskyddsmyndigheten (IMY)

Begäran skickas till: [privacy@beredskapshubben.se](mailto:privacy@beredskapshubben.se)

Vi kan be om identitetsverifiering innan vi lämnar ut personuppgifter.

Vi svarar normalt inom 30 dagar och senast inom en månad enligt GDPR. Vid komplexa ärenden kan svarstiden förlängas med upp till två månader.

---

# 12. Radering av konto

När konto raderas:

* profil tas bort
* personuppgifter raderas eller anonymiseras
* viss data kan sparas om lag kräver det

Gruppinnehåll kan finnas kvar om andra användare påverkas, men anonymiseras.

---

# 13. Barns integritet

Tjänsten är inte avsedd för barn under 16 år.

Om vi upptäcker sådan registrering raderas uppgifter.

---

# 14. Analys och förbättring

Vi använder användningsdata för:

* funktionella förbättringar
* stabilitet
* prestanda
* felanalys

Där möjligt används aggregerad eller pseudonymiserad data.

---

# 15. Notiser och kommunikation

Vi kan skicka:

* säkerhetsmeddelanden
* tjänsteinformation
* driftinformation

Marknadsföring skickas endast om laglig grund finns.

---

# 16. Cookies och spårning

Webbversionen kan använda cookies och liknande teknik.

Se separat Cookiepolicy.

---

# 17. Dataincidenter

Vid personuppgiftsincidenter:

* utreder vi skyndsamt
* rapporterar till IMY vid krav
* informerar berörda användare vid hög risk

---

# 18. Ändringar av policyn

Vi kan uppdatera policyn.

Väsentliga ändringar meddelas via app eller e-post.

---

# 19. Kontakt

[privacy@beredskapshubben.se](mailto:privacy@beredskapshubben.se)

---`;

export const privacyPolicyEN = `# Privacy Policy for Beredskapshubben

**Last updated:** 2026-03-07

Beredskapshubben values your privacy. This Privacy Policy explains how we collect, use, and protect personal data when you use our service.

This policy applies to our website, mobile applications, and related services (the "Service").

---

# 1. Data Controller

The data controller is:

**Beredskapshubben Norden AB**
Company registration number: 559575-3343
Address: Örebro, Sweden
Email: [privacy@beredskapshubben.se](mailto:privacy@beredskapshubben.se)

---

# 2. What Is Personal Data?

Personal data means any information that can directly or indirectly identify a living individual, such as:

* name
* email address
* user ID
* IP address
* device information
* group membership
* in-app behavior

---

# 3. What Data We Collect

## 3.1 Data You Provide

When you register and use the Service:

* name or display name
* email address
* authentication data (email one-time codes; no permanent passwords are stored)
* profile information
* preparedness lists and notes
* inventory records
* family and group information
* messages and chat content
* support requests

---

## 3.2 Data Created Through Use

* activity logs
* feature usage
* timestamps
* change history
* group interactions

---

## 3.3 Technical Data

* IP address
* device type
* operating system
* app version
* session data
* error and crash reports

---

## 3.4 Payment Data

For Premium purchases:

* subscription status
* payment reference
* transaction ID

Card details are handled by external payment providers and are not stored by us.

---

# 4. Purposes of Processing

We process personal data to:

* provide the Service
* create and manage accounts
* enable family and group features
* store checklists and preparedness data
* send notifications and reminders
* process payments
* provide support
* improve functionality and user experience
* prevent abuse and security threats
* comply with legal obligations

---

# 5. Legal Bases Under GDPR

We process personal data based on:

## Contract

To provide the Service under the Terms of Use.

## Legitimate Interest

For:

* security
* abuse prevention
* operational analytics
* product improvement

## Legal Obligation

For accounting and legal requirements.

## Consent

For:

* optional features
* certain analytics
* marketing communications (where applicable)

---

# 6. Social and Group Features

When you use family and group features:

* content is shared with members you invite
* group administrators may see group data
* you are responsible for what you share

We recommend not sharing sensitive personal data.

---

# 7. Data Retention

We retain personal data only as long as necessary:

* Account data: until account deletion
* User content: until deletion request or account closure
* Support cases: up to 24 months
* Payment records: according to accounting law (7 years)
* Security logs: normally 6–12 months

Data may be anonymized for statistics.

---

# 8. Recipients of Data

We may share data with:

## Data Processors

* cloud hosting providers
* infrastructure providers
* analytics services
* email service providers
* payment processors

All processors operate under data processing agreements.

---

## Authorities

When required by law or valid legal request.

---

# 9. International Transfers

If providers are located outside the EU/EEA, we use:

* EU Standard Contractual Clauses
* adequacy decisions
* other approved safeguards

---

# 10. Security Measures

We use technical and organizational safeguards, including:

* encrypted communication (TLS)
* access controls
* logging and monitoring
* role-based permissions
* security monitoring
* protection of authentication data
* least-privilege principles

---

# 11. Your GDPR Rights

You have the right to:

* access your data
* request correction
* request deletion
* restrict processing
* object to processing
* data portability
* withdraw consent
* lodge a complaint with a supervisory authority

Requests can be sent to: [privacy@beredskapshubben.se](mailto:privacy@beredskapshubben.se)

We may request identity verification before disclosing personal data.

We normally respond within 30 days and no later than one month under GDPR. For complex requests, the response time may be extended by up to two additional months.

---

# 12. Account Deletion

When an account is deleted:

* profile data is removed
* personal data is deleted or anonymized
* some data may be retained if legally required

Group content may remain where other users are affected but will be anonymized where possible.

---

# 13. Children's Privacy

The Service is not intended for children under 16.

If such accounts are discovered, data will be deleted.

---

# 14. Analytics and Improvement

We use usage data to improve:

* functionality
* stability
* performance
* error handling

Where possible, aggregated or pseudonymized data is used.

---

# 15. Notifications and Communications

We may send:

* security messages
* service information
* operational notices

Marketing messages are only sent where legally permitted.

---

# 16. Cookies and Tracking

The web version may use cookies and similar technologies.

See separate Cookie Policy.

---

# 17. Data Breaches

In case of a personal data breach:

* we investigate promptly
* report to the supervisory authority where required
* notify affected users where risk is high

---

# 18. Changes to This Policy

We may update this policy.

Material changes will be communicated via the app or email.

---

# 19. Contact

[privacy@beredskapshubben.se](mailto:privacy@beredskapshubben.se)

---`;
